import React, { useState } from 'react'
import Select from 'react-select'
import dayjs from 'dayjs'
import { ReactComponent as CloseIcon } from '../../assets/img/icons/close-icon.svg'
import { ModalWindowCallbackHandler } from '../../store/ModalWindowData/types'
import { EventType, InstitutionDTO, UserDto } from '../../sdk'
import { EventDto } from '../../store/Events/types'
import { EventPatientsField } from './EventPatientsField'

export type ManageEventModalData = {
  event: EventDto | null
  patients: UserDto[]
  institutions: InstitutionDTO[]
}

type ManageEventModalProps = {
  onConfirm: ModalWindowCallbackHandler<'createEvent' | 'updateEvent' | 'cancel'>
  data: ManageEventModalData
}

type DeleteEventModalProps = {
  onConfirm: ModalWindowCallbackHandler<'deleteEvent' | 'cancel'>
  data: { id: string; title: string }
}

type ModalHeaderProps = {
  title: string
  onClose: () => void
}

type FormFieldProps = {
  label: string
  error?: string
  children: React.ReactNode
}

// Value format of datetime-local inputs - local time, without a timezone.
const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm'

function getEndDateError(startDate: string, endDate: string): string | undefined {
  if (!endDate) return 'End date is required'
  if (dayjs(endDate).isBefore(startDate)) return "End date can't be before the start date"
  return undefined
}

export const ModalHeader = ({ title, onClose }: ModalHeaderProps): React.JSX.Element => (
  <div className="note-popup_header flex collapse justify-between align-center">
    <div className="flex-item flex-item--stretch">{title}</div>
    <div className="flex-item flex-item--shrink">
      <button type="button" onClick={onClose}>
        <CloseIcon />
      </button>
    </div>
  </div>
)

export const FormField = ({ label, error, children }: FormFieldProps): React.JSX.Element => (
  <div className="input-holder flex-item" style={{ marginTop: '14px' }}>
    <label>{label}</label>
    {children}
    {error && <span className="login_form-error">{error}</span>}
  </div>
)

export const ManageEventModal = ({ data, onConfirm }: ManageEventModalProps): React.JSX.Element => {
  const { event, patients, institutions } = data

  const [title, setTitle] = useState(event?.title || '')
  const [description, setDescription] = useState(event?.description || '')
  const [startDate, setStartDate] = useState(event ? dayjs(event.startDate).format(DATETIME_LOCAL_FORMAT) : '')
  const [endDate, setEndDate] = useState(event ? dayjs(event.endDate).format(DATETIME_LOCAL_FORMAT) : '')
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(event?.userIds || [])
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState<string[]>(event?.institutionIds || [])
  const [isResolvingPatients, setIsResolvingPatients] = useState(false)

  const titleError = title.trim() ? undefined : 'Title is required'
  const startDateError = startDate ? undefined : 'Start date is required'
  const endDateError = getEndDateError(startDate, endDate)
  const isValid = !titleError && !startDateError && !endDateError

  const institutionOptions = institutions.map(i => ({ value: i.id, label: i.name }))

  const handleCancel = () => onConfirm({ action: 'cancel', data: null })

  const handleSubmit = () => {
    if (!isValid) return

    const dto = {
      type: EventType.CUSTOM_EVENT,
      title: title.trim(),
      description: description.trim(),
      // dayjs reads the input values as local time
      startDate: dayjs(startDate).toISOString(),
      endDate: dayjs(endDate).toISOString(),
      userIds: selectedUserIds,
      institutionIds: selectedInstitutionIds,
      metadata: {},
    }

    if (event) {
      onConfirm({ action: 'updateEvent', data: { id: event.id, ...dto } })
    } else {
      onConfirm({ action: 'createEvent', data: dto })
    }
  }

  return (
    <div className="event-modal">
      <ModalHeader title={event ? 'Edit event' : 'Create event'} onClose={handleCancel} />

      <div className="event-modal_body">
        <div className="input-holder flex-item item-sm-6">
          <FormField label="Title *" error={titleError}>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Event title" />
          </FormField>

          <FormField label="Description">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              style={{ width: '100%', padding: '8px', resize: 'vertical' }}
            />
          </FormField>

          <FormField label="Start date & time *" error={startDateError}>
            <input type="datetime-local" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </FormField>

          <FormField label="End date & time *" error={endDateError}>
            <input type="datetime-local" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </FormField>

          <FormField label="Patients">
            <EventPatientsField
              preloadedPatients={patients}
              selectedIds={selectedUserIds}
              onSelectedIdsChange={setSelectedUserIds}
              onResolvingChange={setIsResolvingPatients}
            />
          </FormField>
            <FormField label="Institutions">
              <Select
                isMulti
                name="institutions"
                options={institutionOptions}
                value={institutionOptions.filter(o => selectedInstitutionIds.includes(o.value))}
                onChange={selected => setSelectedInstitutionIds(selected.map(s => s.value))}
                placeholder="Select institutions..."
              />
            </FormField>
        </div>
      </div>

      <div className="note-popup_footer" style={{ marginTop: '20px' }}>
        <button className="g_btn g_btn--primary" onClick={handleSubmit} disabled={!isValid || isResolvingPatients}>
          Save
        </button>
        <button className="g_btn g_btn--outlined" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export const DeleteEventModal = ({ data, onConfirm }: DeleteEventModalProps): React.JSX.Element => {
  const handleCancel = () => onConfirm({ action: 'cancel', data: null })

  return (
    <div>
      <ModalHeader title="Delete event" onClose={handleCancel} />

      <div style={{ padding: '16px 0' }}>
        Are you sure you want to delete <strong>&ldquo;{data.title}&rdquo;</strong>? This will also cancel all scheduled notifications.
      </div>

      <div className="note-popup_footer">
        <button
          className="g_btn g_btn--primary"
          style={{ background: '#BA1717', borderColor: '#BA1717' }}
          onClick={() => onConfirm({ action: 'deleteEvent', data: data.id })}
        >
          Delete
        </button>
        <button className="g_btn g_btn--outlined" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}
